import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Sparkles, Upload, Eye, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Inbox, ClipboardList, Loader2, FileText, Settings2, Shield, KeyRound,
} from "lucide-react";

/* =========================================================================
   Utilitaires UI
   ========================================================================= */

const CONF_TONE = { high: "success", medium: "warning", low: "danger" };
const CONF_LABEL = { high: "Confiant", medium: "À vérifier", low: "Incertain" };

function ConfidenceBadge({ bucket }) {
  const b = bucket || "low";
  return <StatusBadge tone={CONF_TONE[b]}>{CONF_LABEL[b]}</StatusBadge>;
}

const FIELD_LABELS = {
  supplier: "Fournisseur",
  invoice_number: "N° facture / référence",
  document_date: "Date du document",
  due_date: "Date d'échéance",
  period_start: "Début période",
  period_end: "Fin période",
  amount_ht: "Montant HT (EUR)",
  vat_rate: "Taux TVA (%)",
  vat_amount: "Montant TVA (EUR)",
  amount_ttc: "Montant TTC (EUR)",
  payment_status: "Statut paiement",
  payment_method: "Moyen de paiement",
  customer_reference: "Référence client",
  property_address: "Adresse du bien",
  meter_number: "N° compteur",
  index_previous: "Index précédent",
  index_current: "Index actuel",
  consumption_m3: "Consommation (m³)",
  consumption_kwh: "Consommation (kWh)",
  subscription_amount: "Abonnement (EUR)",
  year: "Année",
  teom_amount: "TEOM (EUR)",
  commune: "Commune",
};

const FIELD_ORDER = [
  "supplier", "invoice_number", "document_date", "due_date",
  "period_start", "period_end",
  "amount_ht", "vat_rate", "vat_amount", "amount_ttc",
  "payment_status", "payment_method",
  "customer_reference", "property_address",
  "year", "commune", "teom_amount",
  "meter_number", "index_previous", "index_current",
  "consumption_m3", "consumption_kwh", "subscription_amount",
];

function fmt(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

/* =========================================================================
   Page principale
   ========================================================================= */

export default function AnalysisPage() {
  const [tab, setTab] = useState("inbox");

  return (
    <div className="animate-fade-up" data-testid="analysis-page">
      <PageHeader
        overline="Assistant IA"
        title="Analyse intelligente des documents"
        description="Déposez vos factures, avis, appels de charges. L'IA extrait automatiquement les données, propose la société / le bien / la catégorie, et rien n'est enregistré sans votre validation."
        actions={<UploadAnalysisDialog onCreated={() => window.dispatchEvent(new Event("analysis:refresh"))} />}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            <Inbox className="h-3.5 w-3.5 mr-1.5" />À valider
          </TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" />Vue d{"'"}ensemble
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <FileText className="h-3.5 w-3.5 mr-1.5" />Historique
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings2 className="h-3.5 w-3.5 mr-1.5" />Paramètres
          </TabsTrigger>
        </TabsList>
        <TabsContent value="inbox"><InboxTab /></TabsContent>
        <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =========================================================================
   Upload
   ========================================================================= */

function UploadAnalysisDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ doc_types: [] });
  const [hint, setHint] = useState({ doc_type: "__auto", company: "", building: "", lot: "" });
  const [links, setLinks] = useState({ companies: [], buildings: [], lots: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/analysis/meta").then(({ data }) => setMeta(data)).catch(() => {});
    Promise.all([api.get("/companies"), api.get("/buildings"), api.get("/lots")])
      .then(([c, b, l]) => setLinks({
        companies: c.data.items || [], buildings: b.data.items || [], lots: l.data.items || [],
      })).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/analysis", {
          filename: file.name,
          mime: file.type,
          content_b64: reader.result,
          hint_doc_type: hint.doc_type && hint.doc_type !== "__auto" ? hint.doc_type : undefined,
          hint_company_id: hint.company || undefined,
          hint_building_id: hint.building || undefined,
          hint_lot_id: hint.lot || undefined,
        });
        toast.success("Document soumis à l'analyse — extraction en cours");
        setOpen(false);
        setFile(null);
        onCreated?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Échec de la soumission");
      } finally {
        setSubmitting(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-pill" data-testid="analysis-upload-btn">
          <Sparkles className="mr-2 h-4 w-4" />Analyser un document
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Analyser un document</DialogTitle>
          <DialogDescription>
            PDF, image ou scan. L{"'"}IA proposera type, fournisseur, bien concerné, catégorie et montants.
            Aucune donnée ne sera enregistrée sans votre validation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
          <div>
            <Label>Fichier *</Label>
            <Input
              type="file"
              accept="application/pdf,image/*"
              capture="environment"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="analysis-file"
            />
            {file && (
              <div className="text-xs text-muted-foreground mt-1">
                {file.name} — {Math.round(file.size / 1024)} Ko
              </div>
            )}
          </div>
          <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-3 text-xs text-primary/80 flex items-start gap-2">
            <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <div>
              Indices facultatifs : renseignez-les uniquement si vous connaissez déjà le type/la société/le bien.
              L{"'"}IA fonctionne aussi sans indice.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type suggéré</Label>
              <Select value={hint.doc_type} onValueChange={(v) => setHint({ ...hint, doc_type: v })}>
                <SelectTrigger data-testid="hint-type"><SelectValue placeholder="Détection auto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto">Détection automatique</SelectItem>
                  {(meta.doc_types || []).map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Société</Label>
              <Select value={hint.company} onValueChange={(v) => setHint({ ...hint, company: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {links.companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Immeuble</Label>
              <Select value={hint.building} onValueChange={(v) => setHint({ ...hint, building: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {links.buildings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lot</Label>
              <Select value={hint.lot} onValueChange={(v) => setHint({ ...hint, lot: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {links.lots.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.reference}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={submit} disabled={!file || submitting} data-testid="analysis-submit">
            {submitting ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Envoi</> :
              <><Sparkles className="mr-2 h-3 w-3" />Lancer l{"'"}analyse</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
   Dashboard
   ========================================================================= */

function DashboardTab() {
  const [dash, setDash] = useState(null);
  useEffect(() => {
    const load = () => api.get("/analysis/dashboard/overview").then(({ data }) => setDash(data));
    load();
    const onRef = () => load();
    window.addEventListener("analysis:refresh", onRef);
    return () => window.removeEventListener("analysis:refresh", onRef);
  }, []);
  if (!dash) return <div className="text-muted-foreground">Chargement…</div>;
  const t = dash.totals;
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard name="a-pending" label="En traitement" icon={Loader2} value={t.pending} />
        <KpiCard name="a-awaiting" label="À valider" icon={Inbox} tone="accent" value={t.awaiting_validation} />
        <KpiCard name="a-validated" label="Validés" icon={CheckCircle2} value={t.validated} />
        <KpiCard name="a-rejected" label="Rejetés" icon={XCircle} value={t.rejected} />
        <KpiCard name="a-failed" label="En erreur" icon={AlertTriangle} value={t.failed} />
      </section>
      <section className="card-elevated p-5">
        <div className="font-heading text-base mb-2">Comment ça marche ?</div>
        <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
          <li>Vous déposez un PDF, une image ou un scan de facture / avis / charges.</li>
          <li>Un premier moteur (Gemini 3.1 Pro) lit le document et extrait les champs.</li>
          <li>Un second moteur (Claude Sonnet 4.5) valide le contexte : société, immeuble, lot, catégorie, part récupérable.</li>
          <li>Vous vérifiez les champs, corrigez si besoin, puis validez — les données sont alors injectées dans charges, consommations et coffre-fort.</li>
        </ol>
      </section>
    </div>
  );
}

/* =========================================================================
   Inbox — analyses à valider
   ========================================================================= */

function InboxTab() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const [a, p] = await Promise.all([
      api.get("/analysis?status=awaiting_validation&limit=100"),
      api.get("/analysis?status=processing&limit=50"),
    ]);
    const pending = await api.get("/analysis?status=pending&limit=50");
    setItems([...(a.data.items || []), ...(p.data.items || []), ...(pending.data.items || [])]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onRef = () => load();
    window.addEventListener("analysis:refresh", onRef);
    return () => window.removeEventListener("analysis:refresh", onRef);
  }, [load]);

  // Poll toutes les 4s tant qu'il y a des analyses en cours de traitement
  useEffect(() => {
    const inFlight = items.some((it) => ["pending", "processing"].includes(it.status));
    if (!inFlight) return;
    const timer = setTimeout(() => {
      setPollCount((c) => c + 1);
      load();
    }, 4000);
    return () => clearTimeout(timer);
  }, [items, pollCount, load]);

  return (
    <div className="space-y-4">
      {loading && items.length === 0 && (
        <div className="text-muted-foreground text-sm flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />Chargement des analyses…
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="card-elevated p-12 text-center text-muted-foreground">
          <Sparkles className="mx-auto h-8 w-8 mb-3 opacity-60" />
          <div className="font-heading text-base">Aucun document à traiter</div>
          <div className="text-sm mt-1">Cliquez sur « Analyser un document » pour démarrer.</div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it) => (
          <AnalysisCard key={it.id} item={it} onOpen={() => setDetail(it.id)} onRefresh={load} />
        ))}
      </div>
      {detail && (
        <AnalysisDetailDialog
          analysisId={detail}
          onClose={() => setDetail(null)}
          onAction={() => { setDetail(null); load(); window.dispatchEvent(new Event("analysis:refresh")); }}
        />
      )}
    </div>
  );
}

function AnalysisCard({ item, onOpen, onRefresh }) {
  const type = item.detected_doc_type || item.hint_doc_type || "…";
  const supplier =
    item.supplier_normalized ||
    item.fields?.supplier?.value ||
    "—";
  const amount = item.fields?.amount_ttc?.value || item.fields?.amount_ht?.value;
  const statusTone = {
    pending: "info", processing: "info",
    awaiting_validation: "warning",
    validated: "success", rejected: "danger", failed: "danger",
  }[item.status] || "neutral";
  const statusLabel = {
    pending: "En file d'attente", processing: "Extraction en cours…",
    awaiting_validation: "À valider",
    validated: "Validé", rejected: "Rejeté", failed: "Erreur",
  }[item.status] || item.status;

  const anomalies = item.anomalies || [];

  const reprocess = async () => {
    try {
      await api.post(`/analysis/${item.id}/reprocess`);
      toast.success("Relance de l'analyse");
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div
      className="card-elevated p-5 space-y-3 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onOpen}
      data-testid={`analysis-card-${item.id}`}
    >
      <div className="flex justify-between items-start">
        <div className="min-w-0">
          <div className="font-heading text-base truncate">{supplier}</div>
          <div className="text-xs text-muted-foreground truncate">{item.filename}</div>
        </div>
        <StatusBadge tone={statusTone}>{statusLabel}</StatusBadge>
      </div>
      <div className="flex justify-between text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Type détecté</div>
          <div className="font-medium">{type}</div>
        </div>
        {amount != null && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Montant</div>
            <div className="font-mono font-semibold">{Number(amount).toFixed(2)} €</div>
          </div>
        )}
      </div>
      {anomalies.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 flex items-start gap-1.5">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <div>
            {anomalies.length} anomalie{anomalies.length > 1 ? "s" : ""} à vérifier
          </div>
        </div>
      )}
      {item.status === "failed" && (
        <div className="space-y-2">
          <div className="text-xs text-red-700 truncate" title={item.error}>
            {item.error || "Erreur pendant l'extraction"}
          </div>
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); reprocess(); }} data-testid={`reprocess-${item.id}`}>
            <RefreshCw className="h-3 w-3 mr-1" />Relancer
          </Button>
        </div>
      )}
      {["pending", "processing"].includes(item.status) && (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />Extraction IA en cours…
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   Détail — split view : document + champs éditables + validation
   ========================================================================= */

function AnalysisDetailDialog({ analysisId, onClose, onAction }) {
  const [analysis, setAnalysis] = useState(null);
  const [meta, setMeta] = useState({ doc_types: [] });
  const [links, setLinks] = useState({ companies: [], buildings: [], lots: [] });
  const [saving, setSaving] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [editedSugg, setEditedSugg] = useState({});
  const [showRawOcr, setShowRawOcr] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [pendingValidatePayload, setPendingValidatePayload] = useState(null);

  useEffect(() => {
    let ok = true;
    Promise.all([
      api.get(`/analysis/${analysisId}`),
      api.get("/analysis/meta"),
      api.get("/companies"),
      api.get("/buildings"),
      api.get("/lots"),
    ]).then(([a, m, co, bu, lo]) => {
      if (!ok) return;
      setAnalysis(a.data);
      setMeta(m.data);
      setLinks({
        companies: co.data.items || [],
        buildings: bu.data.items || [],
        lots: lo.data.items || [],
      });
    });
    return () => { ok = false; };
  }, [analysisId]);

  const merged = useMemo(() => {
    if (!analysis) return null;
    return {
      ...analysis,
      fields: { ...(analysis.fields || {}), ...editedFields },
      suggestions: { ...(analysis.suggestions || {}), ...editedSugg },
    };
  }, [analysis, editedFields, editedSugg]);

  if (!analysis) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-6xl">
          <div className="p-12 text-center text-muted-foreground">
            <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const type = merged.detected_doc_type || "—";
  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/analysis/${analysisId}/file`;

  const setField = (name, value) => {
    setEditedFields((prev) => ({
      ...prev,
      [name]: {
        ...(merged.fields?.[name] || {}),
        value, user_edited: true, status: "validated",
        confidence_bucket: "high",
      },
    }));
  };
  const setSugg = (key, value) => setEditedSugg((prev) => ({ ...prev, [key]: value }));

  const saveAll = async () => {
    setSaving(true);
    try {
      const payload = { fields: {}, ...editedSugg };
      Object.keys(editedFields).forEach((k) => {
        payload.fields[k] = {
          value: editedFields[k].value,
          validated: true,
        };
      });
      if (Object.keys(payload.fields).length === 0) delete payload.fields;
      await api.patch(`/analysis/${analysisId}`, payload);
      toast.success("Corrections enregistrées");
      setEditedFields({});
      setEditedSugg({});
      const { data } = await api.get(`/analysis/${analysisId}`);
      setAnalysis(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const performValidate = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/analysis/${analysisId}/validate`, payload);
      toast.success(
        data.learned_mapping_id
          ? "Analyse validée — la correspondance fournisseur a été mémorisée."
          : "Analyse validée — données propagées."
      );
      onAction?.(data);
      setDuplicateOpen(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Doublon détecté : ouvre la modale de confirmation
      if (e.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_detected") {
        setDuplicates(detail.duplicates || []);
        setPendingValidatePayload(payload);
        setDuplicateOpen(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur validation");
      }
    } finally { setSaving(false); }
  };

  const doValidate = async () => {
    setSaving(true);
    try {
      // Sauve d'abord les éventuelles retouches de champs
      const payload = { fields: {} };
      Object.keys(editedFields || {}).forEach((k) => {
        payload.fields[k] = { value: editedFields[k].value, validated: true };
      });
      if (Object.keys(payload.fields).length === 0) delete payload.fields;
      if (Object.keys(payload).length > 0) await api.patch(`/analysis/${analysisId}`, payload);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement retouches");
      setSaving(false);
      return;
    }
    setSaving(false);
    await performValidate({
      apply_targets: ["charges", "consumption", "vault_metadata"],
      dry_run: false,
      confirm_duplicate: false,
      remember_mapping: true,
    });
  };

  const confirmDuplicateAndValidate = async () => {
    if (!pendingValidatePayload) return;
    await performValidate({ ...pendingValidatePayload, confirm_duplicate: true });
  };

  const doReject = async () => {
    if (!window.confirm("Rejeter cette analyse ?")) return;
    await api.post(`/analysis/${analysisId}/reject`);
    toast.success("Analyse rejetée");
    onAction?.();
  };

  const doReprocess = async () => {
    await api.post(`/analysis/${analysisId}/reprocess`);
    toast.success("Relance en cours");
    onAction?.();
  };

  const fieldsToShow = FIELD_ORDER.filter(
    (fn) => (merged.fields || {})[fn] !== undefined
  );

  const isReadOnly = ["validated", "rejected"].includes(analysis.status);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Sparkles className="h-4 w-4 text-primary" />
            {merged.supplier_normalized || merged.fields?.supplier?.value || analysis.filename}
            <StatusBadge tone={type === "—" ? "neutral" : "accent"}>{type}</StatusBadge>
          </DialogTitle>
          <DialogDescription>
            Vérifiez chaque champ. Rien n{"'"}est enregistré sans votre validation finale.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-h-[70vh] overflow-hidden">
          {/* PANE GAUCHE : document original */}
          <div className="rounded-xl border overflow-hidden bg-secondary/30 flex flex-col min-h-[500px]">
            <div className="px-4 py-2 bg-secondary/70 text-xs uppercase tracking-widest text-muted-foreground border-b flex items-center justify-between">
              <span>Document original</span>
              {analysis.raw_ocr_text && (
                <button
                  onClick={() => setShowRawOcr((s) => !s)}
                  className="text-xs underline"
                  data-testid="toggle-ocr"
                >
                  {showRawOcr ? "Masquer OCR" : "Voir texte OCR"}
                </button>
              )}
            </div>
            {analysis.storage_path || analysis.inline_b64 ? (
              (analysis.mime || "").includes("pdf") ? (
                <iframe
                  src={fileUrl}
                  title="document"
                  className="flex-1 w-full"
                  data-testid="doc-pdf-frame"
                />
              ) : (
                <img
                  src={fileUrl}
                  alt="Aperçu du document"
                  className="flex-1 w-full object-contain bg-white"
                  data-testid="doc-image"
                />
              )
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                Aperçu non disponible
              </div>
            )}
            {showRawOcr && analysis.raw_ocr_text && (
              <div className="max-h-48 overflow-y-auto border-t px-4 py-3 text-xs font-mono whitespace-pre-wrap bg-white">
                {analysis.raw_ocr_text}
              </div>
            )}
          </div>

          {/* PANE DROITE : champs et suggestions */}
          <div className="rounded-xl border overflow-y-auto">
            {/* Anomalies */}
            {(merged.anomalies || []).length > 0 && (
              <div className="p-3 border-b bg-amber-50">
                <div className="flex items-center gap-2 font-heading text-sm text-amber-900 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  {(merged.anomalies || []).length} anomalie(s) — à vérifier, ce ne sont pas des certitudes
                </div>
                <ul className="text-xs space-y-1 list-disc list-inside text-amber-800">
                  {(merged.anomalies || []).map((a, i) => (
                    <li key={`anom-${a.severity || "info"}-${i}`}><span className="font-medium">{a.severity || "info"}:</span> {a.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Suggestions */}
            <div className="p-4 space-y-3 border-b">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Suggestions IA (à confirmer)</div>
                {merged.learned_mapping && merged.learned_mapping.applied_fields?.length > 0 && (
                  <div
                    data-testid="ai-learned-badge"
                    title={`Pré-rempli automatiquement à partir des ${merged.learned_mapping.usage_count} précédent(s) documents de ce fournisseur`}
                    className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent"
                  >
                    <Sparkles className="h-3 w-3" />
                    IA apprise
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type de document</Label>
                  <Select
                    value={merged.detected_doc_type || ""}
                    onValueChange={(v) => setEditedSugg((p) => ({ ...p, detected_doc_type: v }))}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger data-testid="sugg-type"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(meta.doc_types || []).map((t) => (
                        <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Société</Label>
                  <Select
                    value={merged.suggestions?.company_id || "__none"}
                    onValueChange={(v) => setSugg("company_id", v === "__none" ? null : v)}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger data-testid="sugg-company"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">—</SelectItem>
                      {links.companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Immeuble</Label>
                  <Select
                    value={merged.suggestions?.building_id || "__none"}
                    onValueChange={(v) => setSugg("building_id", v === "__none" ? null : v)}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger data-testid="sugg-building"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">—</SelectItem>
                      {links.buildings.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Lot</Label>
                  <Select
                    value={merged.suggestions?.lot_id || "__none"}
                    onValueChange={(v) => setSugg("lot_id", v === "__none" ? null : v)}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger data-testid="sugg-lot"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">—</SelectItem>
                      {links.lots.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.reference}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Catégorie</Label>
                  <Input
                    value={merged.suggestions?.category || ""}
                    onChange={(e) => setSugg("category", e.target.value)}
                    disabled={isReadOnly}
                    data-testid="sugg-category"
                  />
                </div>
                <div>
                  <Label className="text-xs">Récupérable ?</Label>
                  <Select
                    value={
                      merged.suggestions?.recoverable === true ? "yes" :
                        merged.suggestions?.recoverable === false ? "no" : "__none"
                    }
                    onValueChange={(v) =>
                      setSugg("recoverable", v === "yes" ? true : v === "no" ? false : null)
                    }
                    disabled={isReadOnly}
                  >
                    <SelectTrigger data-testid="sugg-recov"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Non défini</SelectItem>
                      <SelectItem value="yes">Oui — récupérable</SelectItem>
                      <SelectItem value="no">Non — à la charge du bailleur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Exercice comptable</Label>
                  <Input
                    value={merged.suggestions?.exercice_comptable || ""}
                    onChange={(e) => setSugg("exercice_comptable", e.target.value)}
                    disabled={isReadOnly}
                    data-testid="sugg-exercice"
                  />
                </div>
                <div>
                  <Label className="text-xs">Clé de répartition</Label>
                  <Select
                    value={merged.suggestions?.allocation_key || "none"}
                    onValueChange={(v) => setSugg("allocation_key", v)}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger data-testid="sugg-alloc"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      <SelectItem value="equal">Parts égales</SelectItem>
                      <SelectItem value="surface">Surface</SelectItem>
                      <SelectItem value="tantiemes">Tantièmes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Commentaire</Label>
                  <Textarea
                    value={merged.suggestions?.commentaire || ""}
                    onChange={(e) => setSugg("commentaire", e.target.value)}
                    disabled={isReadOnly}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Champs extraits */}
            <div className="p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Données extraites</div>
              {fieldsToShow.length === 0 && (
                <div className="text-sm text-muted-foreground">
                  {analysis.status === "processing" || analysis.status === "pending"
                    ? "Extraction en cours…"
                    : "Aucune donnée extraite."}
                </div>
              )}
              <div className="space-y-2">
                {fieldsToShow.map((fn) => {
                  const f = merged.fields[fn];
                  return (
                    <div key={fn} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
                      <div className="text-xs">
                        <div className="font-medium">{FIELD_LABELS[fn] || fn}</div>
                        {f.source && (
                          <div className="text-[10px] text-muted-foreground italic truncate" title={f.source}>
                            « {f.source} »
                          </div>
                        )}
                      </div>
                      <Input
                        value={f.value ?? ""}
                        onChange={(e) => setField(fn, e.target.value)}
                        disabled={isReadOnly}
                        data-testid={`field-${fn}`}
                        className={f.user_edited ? "border-emerald-400" : ""}
                      />
                      <ConfidenceBadge bucket={f.confidence_bucket || "low"} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {analysis.status === "failed" && (
            <Button variant="outline" onClick={doReprocess}>
              <RefreshCw className="h-3 w-3 mr-1" />Relancer l{"'"}analyse
            </Button>
          )}
          {analysis.status === "awaiting_validation" && !isReadOnly && (
            <>
              <Button variant="ghost" onClick={onClose}>Fermer</Button>
              <Button variant="outline" onClick={doReject} data-testid="reject-btn">
                <XCircle className="h-3 w-3 mr-1" />Rejeter
              </Button>
              <Button
                variant="outline"
                onClick={saveAll}
                disabled={saving || (Object.keys(editedFields).length === 0 && Object.keys(editedSugg).length === 0)}
                data-testid="save-corrections-btn"
              >
                Enregistrer les corrections
              </Button>
              <Button onClick={doValidate} disabled={saving} data-testid="validate-btn">
                {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                Valider et enregistrer
              </Button>
            </>
          )}
          {isReadOnly && (
            <Button variant="ghost" onClick={onClose}>Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Modale de confirmation en cas de doublon détecté */}
      <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
        <DialogContent className="max-w-2xl" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Attention — doublon(s) détecté(s)
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Cette facture ressemble fortement à {duplicates.length} document{duplicates.length > 1 ? "s" : ""} déjà présent{duplicates.length > 1 ? "s" : ""}.
            Vérifiez avant de valider pour éviter toute double comptabilisation.
          </p>
          <div className="max-h-60 overflow-y-auto space-y-2 mt-3">
            {duplicates.map((d) => (
              <div key={d.analysis_id} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                <div className="font-medium">{d.supplier} — {d.amount_ttc != null ? `${d.amount_ttc} €` : "—"}</div>
                <div className="text-xs text-muted-foreground">
                  Facture : {d.invoice_number || "—"} · Date : {d.document_date || "—"} · Statut : {d.status}
                </div>
                <div className="text-xs mt-1 flex flex-wrap gap-1">
                  {(d.reasons || []).map((r) => (
                    <span key={r} className="rounded-full bg-amber-200/60 px-2 py-0.5 text-amber-900">{r}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setDuplicateOpen(false)} data-testid="duplicate-cancel">
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmDuplicateAndValidate} data-testid="duplicate-confirm">
              Valider malgré tout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

/* =========================================================================
   Historique
   ========================================================================= */

function HistoryTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("__all");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter !== "__all") params.status = statusFilter;
    const { data } = await api.get("/analysis", { params });
    setItems(data.items || []);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-xs">Statut</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-52" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tous</SelectItem>
              <SelectItem value="validated">Validés</SelectItem>
              <SelectItem value="awaiting_validation">À valider</SelectItem>
              <SelectItem value="rejected">Rejetés</SelectItem>
              <SelectItem value="failed">En erreur</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-muted-foreground">{items.length} document(s)</div>
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left">
              <th className="px-6 py-3">Document</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Statut</th>
              <th className="px-6 py-3">Montant</th>
              <th className="px-6 py-3">Date</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">Aucun document.</td></tr>
            )}
            {items.map((it) => {
              const amount = it.fields?.amount_ttc?.value || it.fields?.amount_ht?.value;
              return (
                <tr key={it.id} className="hover:bg-secondary/30" data-testid={`hist-row-${it.id}`}>
                  <td className="px-6 py-3">
                    <div className="font-medium">{it.supplier_normalized || it.fields?.supplier?.value || it.filename}</div>
                    <div className="text-xs text-muted-foreground">{it.filename}</div>
                  </td>
                  <td className="px-6 py-3 text-xs">{it.detected_doc_type || "—"}</td>
                  <td className="px-6 py-3"><StatusBadge tone={{ validated: "success", rejected: "danger", failed: "danger", awaiting_validation: "warning" }[it.status] || "neutral"}>{it.status}</StatusBadge></td>
                  <td className="px-6 py-3 font-mono">{amount != null ? `${Number(amount).toFixed(2)} €` : "—"}</td>
                  <td className="px-6 py-3 text-xs text-muted-foreground">{new Date(it.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="px-6 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDetail(it.id)}><Eye className="h-3 w-3" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {detail && (
        <AnalysisDetailDialog
          analysisId={detail}
          onClose={() => setDetail(null)}
          onAction={() => { setDetail(null); load(); }}
        />
      )}
    </div>
  );
}

/* =========================================================================
   Paramètres
   ========================================================================= */

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/analysis/settings");
    setSettings(data);
    setForm({
      anomaly_threshold_default: data.anomaly_threshold_default,
      thr_eau: data.thresholds_by_energy?.eau,
      thr_elec: data.thresholds_by_energy?.electricite,
      thr_gaz: data.thresholds_by_energy?.gaz,
      ocr_provider: data.ocr_provider,
      ocr_model: data.ocr_model,
      validator_provider: data.validator_provider,
      validator_model: data.validator_model,
      custom_gemini_key: "",
      custom_anthropic_key: "",
      custom_openai_key: "",
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        anomaly_threshold_default: parseFloat(form.anomaly_threshold_default),
        thresholds_by_energy: {
          eau: parseFloat(form.thr_eau),
          electricite: parseFloat(form.thr_elec),
          gaz: parseFloat(form.thr_gaz),
        },
        ocr_provider: form.ocr_provider,
        ocr_model: form.ocr_model,
        validator_provider: form.validator_provider,
        validator_model: form.validator_model,
      };
      if (form.custom_gemini_key) payload.custom_gemini_key = form.custom_gemini_key;
      if (form.custom_anthropic_key) payload.custom_anthropic_key = form.custom_anthropic_key;
      if (form.custom_openai_key) payload.custom_openai_key = form.custom_openai_key;
      await api.patch("/analysis/settings", payload);
      toast.success("Paramètres enregistrés");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="card-elevated p-5 space-y-4">
        <div className="font-heading text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />Seuils d{"'"}anomalies (comparaison consommation)
        </div>
        <div className="text-sm text-muted-foreground">
          Écart au-delà duquel une hausse est signalée comme <em>à vérifier</em>. Toujours présentée comme une anomalie possible, jamais comme une certitude.
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Défaut (30% = 0.30)</Label>
            <Input type="number" step="0.05" min="0.05" max="5"
              value={form.anomaly_threshold_default || ""}
              onChange={(e) => setForm({ ...form, anomaly_threshold_default: e.target.value })}
              data-testid="thr-default" />
          </div>
          <div>
            <Label>Eau</Label>
            <Input type="number" step="0.05" value={form.thr_eau || ""}
              onChange={(e) => setForm({ ...form, thr_eau: e.target.value })} data-testid="thr-eau" />
          </div>
          <div>
            <Label>Électricité</Label>
            <Input type="number" step="0.05" value={form.thr_elec || ""}
              onChange={(e) => setForm({ ...form, thr_elec: e.target.value })} data-testid="thr-elec" />
          </div>
          <div>
            <Label>Gaz</Label>
            <Input type="number" step="0.05" value={form.thr_gaz || ""}
              onChange={(e) => setForm({ ...form, thr_gaz: e.target.value })} data-testid="thr-gaz" />
          </div>
        </div>
      </div>

      <div className="card-elevated p-5 space-y-4">
        <div className="font-heading text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4" />Moteurs IA
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <Label>OCR provider</Label>
            <Input value={form.ocr_provider || ""} onChange={(e) => setForm({ ...form, ocr_provider: e.target.value })} />
          </div>
          <div>
            <Label>OCR model</Label>
            <Input value={form.ocr_model || ""} onChange={(e) => setForm({ ...form, ocr_model: e.target.value })} />
          </div>
          <div>
            <Label>Validator provider</Label>
            <Input value={form.validator_provider || ""} onChange={(e) => setForm({ ...form, validator_provider: e.target.value })} />
          </div>
          <div>
            <Label>Validator model</Label>
            <Input value={form.validator_model || ""} onChange={(e) => setForm({ ...form, validator_model: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="card-elevated p-5 space-y-4">
        <div className="font-heading text-base flex items-center gap-2">
          <KeyRound className="h-4 w-4" />Clés API personnalisées (facultatif)
        </div>
        <div className="text-sm text-muted-foreground flex items-start gap-2">
          <Shield className="h-3.5 w-3.5 mt-0.5" />
          <div>
            Par défaut : clé universelle Emergent {settings.using_emergent_key ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="warning">Override</StatusBadge>}. Vous pouvez fournir vos propres clés pour utiliser vos comptes OpenAI / Anthropic / Gemini.
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label>Clé Gemini {settings.custom_gemini_key && <span className="text-xs text-muted-foreground">(actuelle : {settings.custom_gemini_key})</span>}</Label>
            <Input type="password" placeholder="Laisser vide pour conserver / clé Emergent"
              value={form.custom_gemini_key || ""}
              onChange={(e) => setForm({ ...form, custom_gemini_key: e.target.value })} data-testid="key-gemini" />
          </div>
          <div>
            <Label>Clé Anthropic {settings.custom_anthropic_key && <span className="text-xs text-muted-foreground">(actuelle : {settings.custom_anthropic_key})</span>}</Label>
            <Input type="password" placeholder="Laisser vide pour conserver / clé Emergent"
              value={form.custom_anthropic_key || ""}
              onChange={(e) => setForm({ ...form, custom_anthropic_key: e.target.value })} data-testid="key-anthropic" />
          </div>
          <div>
            <Label>Clé OpenAI {settings.custom_openai_key && <span className="text-xs text-muted-foreground">(actuelle : {settings.custom_openai_key})</span>}</Label>
            <Input type="password" placeholder="Laisser vide pour conserver / clé Emergent"
              value={form.custom_openai_key || ""}
              onChange={(e) => setForm({ ...form, custom_openai_key: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="settings-save">
          {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
          Enregistrer les paramètres
        </Button>
      </div>
    </div>
  );
}
